import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ['results', 'loading']
    static values = {
        context: String,
        currentPage: { type: Number, default: 1 }
    }

    connect() {
        this.setupEventListeners()

        // Charger les résultats selon les paramètres URL ou tous les éléments
        this.loadInitialResults()
    }

    setupEventListeners() {
        // Écouter les changements sur tous les inputs de filtres
        this.element.addEventListener('change', (event) => {
            if (event.target.matches('input, select')) {
                this.currentPageValue = 1
                this.debounce(() => this.applyFilters(), 300)
            }
        })

        // Écouter les inputs de type range et text avec un délai
        this.element.addEventListener('input', (event) => {
            if (event.target.matches('input[type="number"], input[type="date"]')) {
                this.currentPageValue = 1
                this.debounce(() => this.applyFilters(), 500)
            }
        })

        // Gestion de la pagination
        this.element.addEventListener('click', (event) => {
            if (event.target.matches('.c-pagination__item[data-page]')) {
                this.goToPage(event)
            }
        })
    }

    applyFilters() {
        this.showLoading()
        const filters = this.collectFilters()

        const formData = new FormData()
        formData.append('action', this.getAjaxAction())
        formData.append('paged', this.currentPageValue)

        // Ajouter chaque filtre au FormData
        Object.entries(filters).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(v => formData.append(`filters[${key}][]`, v))
            } else {
                formData.append(`filters[${key}]`, value)
            }
        })

        fetch(modularFiltersAjax.ajaxUrl, {
            method: 'POST',
            body: formData
        })
        .then(response => response.json())
        .then(data => {
            this.hideLoading()
            if (data.success) {
                this.updateResults(data.data.html)
                this.updateUrl(filters)
                this.dispatchFilterEvent('applied', { filters, results: data.data })
            } else {
                this.showError('Une erreur est survenue lors du filtrage.')
            }
        })
        .catch(error => {
            this.hideLoading()
            this.showError('Erreur de connexion.')
        })
    }

    resetFilters() {
        // Réinitialiser tous les inputs
        this.element.querySelectorAll('input[type="checkbox"]').forEach(input => {
            input.checked = false
        })

        this.element.querySelectorAll('select').forEach(select => {
            select.selectedIndex = 0
        })

        this.element.querySelectorAll('input[type="number"], input[type="date"]').forEach(input => {
            input.value = ''
        })

        this.currentPageValue = 1
        this.applyFilters()
        this.dispatchFilterEvent('reset')
    }

    collectFilters() {
        const filters = {}

        // Ajouter d'abord les filtres cachés
        const hiddenFilters = this.getHiddenFilters()
        Object.assign(filters, hiddenFilters)

        // Checkboxes
        this.element.querySelectorAll('input[type="checkbox"]:checked').forEach(input => {
            const name = input.name.replace(/^filter_/, '').replace(/\[\]$/, '')
            if (!filters[name]) {
                filters[name] = []
            }
            filters[name].push(input.value)
        })

        // Selects
        this.element.querySelectorAll('select').forEach(select => {
            const name = select.name.replace(/^filter_/, '')
            if (select.value) {
                filters[name] = select.value
            }
        })

        // Range inputs
        this.element.querySelectorAll('input[type="number"]').forEach(input => {
            const name = input.name.replace(/^filter_/, '')
            if (input.value) {
                filters[name] = input.value
            }
        })

        // Date inputs
        this.element.querySelectorAll('input[type="date"]').forEach(input => {
            const name = input.name.replace(/^filter_/, '')
            if (input.value) {
                filters[name] = input.value
            }
        })

        return filters
    }

    getHiddenFilters() {
        const hiddenFiltersElement = this.element.closest('[data-hidden-filters]') ||
                                   this.element.querySelector('[data-hidden-filters]')

        if (hiddenFiltersElement && hiddenFiltersElement.dataset.hiddenFilters) {
            try {
                return JSON.parse(hiddenFiltersElement.dataset.hiddenFilters)
            } catch (e) {
                return {}
            }
        }

        return {}
    }

    getAjaxAction() {
        switch (this.contextValue) {
            case 'products':
                return 'filter_products'
            case 'posts':
                return 'filter_posts'
            default:
                return 'filter_products'
        }
    }

    showLoading() {
        if (this.hasLoadingTarget) {
            this.loadingTarget.style.display = 'block'
        }
        this.element.classList.add('filtering')
    }

    hideLoading() {
        if (this.hasLoadingTarget) {
            this.loadingTarget.style.display = 'none'
        }
        this.element.classList.remove('filtering')
    }

    updateResults(html) {
        if (this.hasResultsTarget) {
            this.resultsTarget.innerHTML = html
        } else {
            // Fallback : chercher le conteneur de résultats par attribut data
            const resultsContainer = document.querySelector('[data-filters-results]')
            if (resultsContainer) {
                resultsContainer.innerHTML = html
            }
        }
    }

    updateUrl(filters) {
        const url = new URL(window.location)
        const hiddenFilters = this.getHiddenFilters()

        // Supprimer les anciens paramètres de filtre et pagination
        const keysToDelete = []
        for (const [key] of url.searchParams) {
            if (key.startsWith('filter_') || key === 'paged') {
                keysToDelete.push(key)
            }
        }
        keysToDelete.forEach(key => url.searchParams.delete(key))

        // Ajouter seulement les filtres visibles (pas les cachés)
        Object.entries(filters).forEach(([key, value]) => {
            // Exclure les filtres cachés de l'URL
            if (hiddenFilters[key]) {
                return
            }

            if (value && value.length > 0) { // Ne pas ajouter les filtres vides
                if (Array.isArray(value)) {
                    value.forEach(v => {
                        if (v) url.searchParams.append(`filter_${key}`, v)
                    })
                } else {
                    url.searchParams.set(`filter_${key}`, value)
                }
            }
        })

        // Ajouter la pagination si différente de 1
        if (this.currentPageValue > 1) {
            url.searchParams.set('paged', this.currentPageValue)
        }
    }

    showError(message) {
        const errorDiv = document.createElement('div')
        errorDiv.className = 'modular-filters-error'
        errorDiv.textContent = message
        errorDiv.style.cssText = `
            background: #f8d7da;
            color: #721c24;
            padding: 10px;
            border: 1px solid #f5c6cb;
            border-radius: 4px;
            margin: 10px 0;
        `

        this.element.insertBefore(errorDiv, this.element.firstChild)

        setTimeout(() => {
            errorDiv.remove()
        }, 5000)
    }

    dispatchFilterEvent(type, detail = {}) {
        const event = new CustomEvent(`modular-filters:${type}`, {
            detail: {
                context: this.contextValue,
                ...detail
            },
            bubbles: true
        })
        this.element.dispatchEvent(event)
    }

    loadInitialResults() {
        // Appliquer les filtres (vides ou depuis l'URL)
        this.applyFilters()
    }

    setFilterValue(filterName, values) {
        // Assurer que values est toujours un tableau
        const valueArray = Array.isArray(values) ? values : [values]

        // Checkboxes - chercher par name contenant le filterName
        const checkboxes = this.element.querySelectorAll(`input[type="checkbox"]`)
        checkboxes.forEach(checkbox => {
            const name = checkbox.name
            if (name.includes(filterName)) {
                checkbox.checked = valueArray.includes(checkbox.value)
            }
        })

        // Selects
        const selects = this.element.querySelectorAll(`select`)
        selects.forEach(select => {
            const name = select.name
            if (name.includes(filterName)) {
                const value = valueArray[0] // Pour les selects, prendre la première valeur
                if (select.querySelector(`option[value="${value}"]`)) {
                    select.value = value
                }
            }
        })

        // Input numbers et dates
        const inputs = this.element.querySelectorAll(`input[type="number"], input[type="date"]`)
        inputs.forEach(input => {
            const name = input.name
            if (name.includes(filterName)) {
                input.value = valueArray[0] // Prendre la première valeur
            }
        })
    }

    debounce(func, wait) {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout)
        }
        this.debounceTimeout = setTimeout(func, wait)
    }

    // Actions pour les boutons externes
    applyFiltersAction() {
        this.currentPageValue = 1
        this.applyFilters()
    }

    resetFiltersAction() {
        this.resetFilters()
    }

    // Gestion de la pagination
    goToPage(event) {
        event.preventDefault()
        const page = parseInt(event.target.dataset.page)
        if (page && page !== this.currentPageValue) {
            this.currentPageValue = page
            this.applyFilters()
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }
}